import axios, { type AxiosRequestConfig, type AxiosResponse } from 'axios';
import { ElMessage } from 'element-plus';
import type { ApiResponse } from '@trace/shared/types';
import { useAuthStore } from '../store/useAuthStore';

// Create Axios Instance
export const request = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

    // Request Interceptor
request.interceptors.request.use(
  (config) => {
    // If request payload is FormData, remove Content-Type so browser sets boundary automatically
    if (config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type'];
    }

    // Auth Store check
    try {
      const authStore = useAuthStore();
      if (authStore.token && config.headers) {
        config.headers.Authorization = `Bearer ${authStore.token}`;
      }
      if (authStore.tenantId && config.headers) {
        config.headers['X-Tenant-Id'] = authStore.tenantId;
      }
      const effEnterpriseId = authStore.effectiveEnterpriseId;
      if (effEnterpriseId && effEnterpriseId !== 'all' && config.headers) {
        config.headers['X-Tenant-Enterprise-Id'] = String(effEnterpriseId);
      }
    } catch {
      // Store not ready during early init
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response Interceptor
request.interceptors.response.use(
  (response: AxiosResponse<ApiResponse<unknown>>) => {
    const res = response.data;

    // Direct return if binary/blob or standard format
    if (response.config.responseType === 'blob') {
      return response as unknown as AxiosResponse;
    }

    if (res.code === 200 || res.success) {
      return res.data as unknown as AxiosResponse;
    }

    // Business Error
    ElMessage.error(res.message || '操作失败，请重试');
    return Promise.reject(new Error(res.message || 'Error'));
  },
  (error) => {
    const status = error.response?.status;
    const errorMessage = error.response?.data?.message || error.message || '网络连接异常';

    if (status === 401) {
      ElMessage.error({ message: '登录状态已失效，请重新登录', grouping: true });
      try {
        useAuthStore().logout();
      } catch {}
      // 使用 Router 导航而非 window.location 硬刷新，避免丢失 SPA 状态
      const currentPath = window.location.pathname;
      if (currentPath !== '/login') {
        import('../router').then(({ default: router }) => {
          router.push({ path: '/login', query: { redirect: currentPath } });
        }).catch(() => {
          // Router 未就绪时降级为硬导航
          window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`;
        });
      }
    } else if (status === 403) {
      ElMessage.error({ message: '您暂无权限访问该资源', grouping: true });
    } else if (status >= 500) {
      ElMessage.error({ message: '服务器内部错误，请联系管理员', grouping: true });
    } else {
      ElMessage.error({ message: errorMessage, grouping: true });
    }

    return Promise.reject(error);
  },
);

// Generic Request Helper methods
export async function get<T>(url: string, params?: Record<string, unknown>, config?: AxiosRequestConfig): Promise<T> {
  return request.get<unknown, T>(url, { params, ...config }) as unknown as Promise<T>;
}

export async function post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
  return request.post<unknown, T>(url, data, config) as unknown as Promise<T>;
}

export async function put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
  return request.put<unknown, T>(url, data, config) as unknown as Promise<T>;
}

export async function del<T>(url: string, params?: Record<string, unknown>, config?: AxiosRequestConfig): Promise<T> {
  return request.delete<unknown, T>(url, { params, ...config }) as unknown as Promise<T>;
}
